import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface CallRecording {
  id: string;
  call_id: string;
  twilio_recording_sid: string | null;
  storage_path: string;
  original_url: string | null;
  duration_seconds: number | null;
  format: string | null;
  size_bytes: number | null;
  created_at: string;
}

export interface Call {
  id: string;
  contact_id: string;
  lead_id?: string | null;
  deal_id?: string | null;
  twilio_call_sid: string | null;
  direction: string;
  from_e164: string | null;
  to_e164: string | null;
  status: string | null;
  duration_seconds: number | null;
  started_at: string | null;
  ended_at: string | null;
  handled_by_app_user_id: string | null;
  created_at: string;
  updated_at: string;
  scheduled_for?: string | null;
  title?: string | null;
  call_purpose?: string | null;
  call_agenda?: string | null;
  reminder_time?: string | null;
  reminder_sent?: boolean;
  related_to_type?: string | null;
  related_to_id?: string | null;
  original_scheduled_for?: string | null;
  rescheduled_count?: number;
  channel?: string;
  handler?: {
    display_name: string | null;
    email: string | null;
  };
  recordings?: CallRecording[];
  notes?: string | any;
}

export function useCalls(contactId?: string, leadId?: string, dealId?: string) {
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadCalls = useCallback(async () => {
    if (!contactId && !leadId && !dealId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Loading calls for:', { contactId, leadId });
      
      let allCalls: Call[] = [];

      // If we have a contactId, fetch both contact calls AND calls from leads converted to this contact
      if (contactId) {
        // Get contact calls
        const { data: contactCalls, error: contactError } = await supabase
          .from('calls')
          .select(`
            *,
            recordings:call_recordings(*)
          `)
          .eq('contact_id', contactId)
          .order('created_at', { ascending: false });

        if (contactError) throw contactError;

        // Get calls from leads that were converted to this contact
        // @ts-ignore - TypeScript recursion limit with nested Supabase joins
        const leadCallsQuery = supabase
          .from('calls')
          .select(`
            *,
            recordings:call_recordings(*),
            leads!inner(contact_id)
          `)
          .eq('leads.contact_id', contactId)
          .is('contact_id', null)
          .order('created_at', { ascending: false });
        const { data: leadCalls, error: leadError } = await leadCallsQuery;

        if (leadError) throw leadError;

        // Combine and sort all calls
        allCalls = [...(contactCalls || []), ...(leadCalls || [])]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        // Fallback: buscar chamadas órfãs (sem contact_id) pelo telefone do contato
        try {
          const { data: contactData } = await supabase
            .from('contacts')
            .select('primary_phone')
            .eq('id', contactId)
            .single();

          const phone = contactData?.primary_phone;
          if (phone) {
            const { data: phoneCalls } = await supabase
              .from('calls')
              .select('*, recordings:call_recordings(*)')
              .is('contact_id', null)
              .is('lead_id', null)
              .or(`to_e164.eq.${phone},from_e164.eq.${phone}`)
              .order('created_at', { ascending: false });

            if (phoneCalls?.length) {
              const existingIds = new Set(allCalls.map(c => c.id));
              const orphanCalls = phoneCalls.filter(c => !existingIds.has(c.id));
              allCalls = [...allCalls, ...orphanCalls];
            }
          }
        } catch (phoneErr) {
          console.warn('Fallback phone lookup failed:', phoneErr);
        }

      } else if (leadId) {
        // For leads, just get their calls
        const { data, error: fetchError } = await supabase
          .from('calls')
          .select(`
            *,
            recordings:call_recordings(*)
          `)
          .eq('lead_id', leadId)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        allCalls = data || [];
      }

      // Also fetch calls by deal_id if available
      if (dealId) {
        const { data: dealCalls, error: dealError } = await supabase
          .from('calls')
          .select(`
            *,
            recordings:call_recordings(*)
          `)
          .eq('deal_id', dealId)
          .order('created_at', { ascending: false });

        if (!dealError && dealCalls) {
          // Merge deal calls, avoiding duplicates
          const existingIds = new Set(allCalls.map(c => c.id));
          const newCalls = dealCalls.filter(c => !existingIds.has(c.id));
          allCalls = [...allCalls, ...newCalls];
        }
      }

      // Sort all calls by created_at descending
      allCalls.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      // Attach recordings via separate query to avoid embed issues
      try {
        const callIds = allCalls.map(c => c.id);
        if (callIds.length) {
          const { data: recs, error: recsError } = await supabase
            .from('call_recordings')
            .select('*')
            .in('call_id', callIds);
          if (recsError) throw recsError;

          const byCallId: Record<string, CallRecording[]> = {};
          (recs || []).forEach((r) => {
            if (!byCallId[r.call_id]) byCallId[r.call_id] = [];
            byCallId[r.call_id].push(r as unknown as CallRecording);
          });

          allCalls = allCalls.map((c) => ({
            ...c,
            recordings: byCallId[c.id] || [],
          }));
        }
      } catch (attachErr) {
        console.error('Error attaching recordings:', attachErr);
      }
      
      console.log('Loaded calls data:', allCalls);
      console.log('Number of calls:', allCalls?.length || 0);
      allCalls?.forEach((call, index) => {
        console.log(`Call ${index + 1}:`, {
          id: call.id,
          status: call.status,
          recordingsCount: call.recordings?.length || 0,
          recordings: call.recordings
        });
      });
      
      setCalls(allCalls);
    } catch (err) {
      console.error('Error loading calls:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [contactId, leadId, dealId]);

  const initiateCall = async (phoneNumber: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('twilio-call', {
        body: {
          action: 'initiate',
          contactId,
          leadId,
          phoneNumber,
        },
      });

      if (error) throw error;
      
      await loadCalls();
      return data;
    } catch (err) {
      console.error('Error initiating call:', err);
      throw err;
    }
  };

  const addCallNote = async (callId: string, noteText: string) => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get current call
      const { data: call, error: fetchError } = await supabase
        .from('calls')
        .select('notes')
        .eq('id', callId)
        .single();

      if (fetchError) throw fetchError;

      // Parse existing notes
      let existingNotes = [];
      if (call.notes) {
        try {
          existingNotes = typeof call.notes === 'string' ? JSON.parse(call.notes) : call.notes;
          if (!Array.isArray(existingNotes)) existingNotes = [];
        } catch {
          existingNotes = [];
        }
      }

      // Get app_user info
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id, first_name, last_name, display_name')
        .eq('auth_user_id', user.id)
        .single();

      const userName = appUser?.display_name || 
                       `${appUser?.first_name || ''} ${appUser?.last_name || ''}`.trim() || 
                       user.email || 'Unknown User';

      // Add new note
      const newNote = {
        text: noteText,
        created_by: userName,
        created_by_id: appUser?.id || user.id,
        created_date: new Date().toISOString(),
      };

      const updatedNotes = [...existingNotes, newNote];

      // Update call with new notes array
      const { error: updateError } = await supabase
        .from('calls')
        .update({ notes: JSON.stringify(updatedNotes) })
        .eq('id', callId);

      if (updateError) throw updateError;
      
      await loadCalls();
    } catch (err) {
      console.error('Error adding call note:', err);
      throw err;
    }
  };

  useEffect(() => {
    loadCalls();

    if (!contactId && !leadId && !dealId) return;

    // Subscribe to real-time updates for calls
    const channels: ReturnType<typeof supabase.channel>[] = [];
    
    // Contact channel
    if (contactId) {
      const contactChannel = supabase
        .channel(`calls-contact-${contactId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'calls',
            filter: `contact_id=eq.${contactId}`,
          },
          () => {
            console.log('Call data changed (contact), reloading...');
            loadCalls();
          }
        )
        .subscribe();
      channels.push(contactChannel);
    }
    
    // Lead channel
    if (leadId) {
      const leadChannel = supabase
        .channel(`calls-lead-${leadId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'calls',
            filter: `lead_id=eq.${leadId}`,
          },
          () => {
            console.log('Call data changed (lead), reloading...');
            loadCalls();
          }
        )
        .subscribe();
      channels.push(leadChannel);
    }
    
    // Deal channel
    if (dealId) {
      const dealChannel = supabase
        .channel(`calls-deal-${dealId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'calls',
            filter: `deal_id=eq.${dealId}`,
          },
          () => {
            console.log('Call data changed (deal), reloading...');
            loadCalls();
          }
        )
        .subscribe();
      channels.push(dealChannel);
    }

    return () => {
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, [contactId, leadId, dealId]);

  const scheduleCall = async (callData: {
    contact_id?: string;
    lead_id?: string;
    deal_id?: string;
    scheduled_for: string;
    subject: string;
    call_purpose?: string;
    call_agenda?: string;
    from_e164: string;
    to_e164: string;
    handled_by_app_user_id: string;
    reminder_time?: string;
    related_to_type?: string;
    related_to_id?: string;
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('calls')
        .insert([{
          ...callData,
          direction: 'outbound',
          status: 'scheduled',
          created_by: user.id,
          channel: 'voice',
        }]);

      if (error) throw error;
      
      await loadCalls();
    } catch (err) {
      console.error('Error scheduling call:', err);
      throw err;
    }
  };

  const updateScheduledCall = async (callId: string, updates: Partial<Call>) => {
    try {
      // Check if scheduled_for is being changed to track rescheduling
      if (updates.scheduled_for) {
        const { data: currentCall } = await supabase
          .from('calls')
          .select('scheduled_for, original_scheduled_for, rescheduled_count')
          .eq('id', callId)
          .single();
        
        if (currentCall && currentCall.scheduled_for !== updates.scheduled_for) {
          // Increment rescheduled_count
          (updates as any).rescheduled_count = (currentCall.rescheduled_count || 0) + 1;
          
          // Set original_scheduled_for if this is the first reschedule
          if (!currentCall.original_scheduled_for) {
            (updates as any).original_scheduled_for = currentCall.scheduled_for;
          }
        }
      }
      
      const { error } = await supabase
        .from('calls')
        .update(updates as any)
        .eq('id', callId);

      if (error) throw error;
      
      await loadCalls();
    } catch (err) {
      console.error('Error updating scheduled call:', err);
      throw err;
    }
  };

  const deleteScheduledCall = async (callId: string) => {
    try {
      // First, fetch the call to get google_calendar_event_id and created_by
      const { data: call, error: fetchError } = await supabase
        .from('calls')
        .select('google_calendar_event_id, created_by')
        .eq('id', callId)
        .single();

      if (fetchError) {
        console.error('Error fetching call for deletion:', fetchError);
      }

      // If there's a Google Calendar event, delete it first
      if (call?.google_calendar_event_id && call?.created_by) {
        try {
          console.log('Deleting Google Calendar event:', call.google_calendar_event_id);
          const { error: calError } = await supabase.functions.invoke('google-calendar-delete-event', {
            body: {
              host_user_id: call.created_by,
              event_id: call.google_calendar_event_id,
            },
          });
          if (calError) {
            console.error('Error deleting calendar event:', calError);
          } else {
            console.log('Google Calendar event deleted successfully');
          }
        } catch (calError) {
          console.error('Error calling google-calendar-delete-event:', calError);
        }
      }

      // Now delete the call from the database
      const { error } = await supabase
        .from('calls')
        .delete()
        .eq('id', callId);

      if (error) throw error;
      
      await loadCalls();
    } catch (err) {
      console.error('Error deleting scheduled call:', err);
      throw err;
    }
  };

  const markCallComplete = async (callId: string, notes?: string, completedBy?: string) => {
    try {
      const now = new Date().toISOString();
      const updates: any = {
        status: 'completed',
        ended_at: now,
        completed_at: now,
      };

      if (notes) {
        updates.notes = notes;
      }

      // Track who clicked "Complete" (for auditing)
      if (completedBy) {
        updates.completed_by = completedBy;
      }

      const { error } = await supabase
        .from('calls')
        .update(updates)
        .eq('id', callId);

      if (error) throw error;
      
      await loadCalls();
    } catch (err) {
      console.error('Error marking call complete:', err);
      throw err;
    }
  };

  const cancelScheduledCall = async (callId: string, reason?: string) => {
    try {
      // First, fetch the call to get google_calendar_event_id, created_by, and matching fields
      const { data: call, error: fetchError } = await supabase
        .from('calls')
        .select('google_calendar_event_id, created_by, title, scheduled_for, contact_id')
        .eq('id', callId)
        .single();

      if (fetchError) {
        console.error('Error fetching call for cancellation:', fetchError);
      }

      // If there's a Google Calendar event, delete it first
      if (call?.google_calendar_event_id && call?.created_by) {
        try {
          console.log('Deleting Google Calendar event on cancel:', call.google_calendar_event_id);
          const { error: calError } = await supabase.functions.invoke('google-calendar-delete-event', {
            body: {
              host_user_id: call.created_by,
              event_id: call.google_calendar_event_id,
            },
          });
          if (calError) {
            console.error('Error deleting calendar event:', calError);
          } else {
            console.log('Google Calendar event deleted successfully');
          }
        } catch (calError) {
          console.error('Error calling google-calendar-delete-event:', calError);
        }
      }

      // Now update the call status
      const updates: any = {
        status: 'canceled',
        cancelled_at: new Date().toISOString(),
        cancelled_reason: reason || undefined,
      };

      const { error } = await supabase
        .from('calls')
        .update(updates)
        .eq('id', callId);

      if (error) throw error;

      // Sync: also cancel the corresponding calendar_event (match by title + start_at + contact_id)
      if (call?.title && call?.scheduled_for) {
        try {
          let ceQuery = (supabase as any)
            .from('calendar_events')
            .update({ status: 'canceled' })
            .eq('title', call.title)
            .eq('start_at', call.scheduled_for)
            .neq('status', 'canceled');

          if (call.contact_id) {
            ceQuery = ceQuery.eq('contact_id', call.contact_id);
          }

          const { error: ceError } = await ceQuery;
          if (ceError) {
            console.error('Error canceling matching calendar_event:', ceError);
          } else {
            console.log('✅ Matching calendar_event canceled');
          }
        } catch (ceErr) {
          console.error('Error syncing calendar_event cancel:', ceErr);
        }
      }
      
      await loadCalls();
    } catch (err) {
      console.error('Error canceling scheduled call:', err);
      throw err;
    }
  };

  /**
   * Reschedule a call - creates a historical record with 'rescheduled' status
   * and updates the original call with the new scheduled time
   */
  const rescheduleCall = async (callId: string, newScheduledFor: string, reason?: string) => {
    try {
      // 1. Fetch the current call data
      const { data: currentCall, error: fetchError } = await supabase
        .from('calls')
        .select('*')
        .eq('id', callId)
        .single();
      
      if (fetchError || !currentCall) {
        throw new Error('Call not found');
      }

      // 2. Create a historical record with the old schedule and status 'rescheduled'
      const historicalRecord = {
        contact_id: currentCall.contact_id,
        lead_id: currentCall.lead_id,
        deal_id: currentCall.deal_id,
        direction: currentCall.direction,
        channel: currentCall.channel,
        status: 'rescheduled',
        scheduled_for: currentCall.scheduled_for,
        title: currentCall.title,
        call_purpose: currentCall.call_purpose,
        call_agenda: currentCall.call_agenda,
        from_e164: currentCall.from_e164,
        to_e164: currentCall.to_e164,
        handled_by_app_user_id: currentCall.handled_by_app_user_id,
        created_by: currentCall.created_by,
        notes: reason || `Remarcada para ${new Date(newScheduledFor).toLocaleString('pt-BR')}`,
        ended_at: new Date().toISOString(),
        original_scheduled_for: currentCall.original_scheduled_for || currentCall.scheduled_for,
        rescheduled_count: currentCall.rescheduled_count || 0,
        provider: currentCall.provider || 'twilio',
        meeting_url: currentCall.meeting_url,
        attendees: currentCall.attendees,
      };

      const { error: insertError } = await supabase
        .from('calls')
        .insert(historicalRecord);
      
      if (insertError) {
        console.error('Error creating historical record:', insertError);
        throw insertError;
      }

      // 3. Update the original call with the new schedule
      const updates = {
        scheduled_for: newScheduledFor,
        rescheduled_count: (currentCall.rescheduled_count || 0) + 1,
        original_scheduled_for: currentCall.original_scheduled_for || currentCall.scheduled_for,
      };

      const { error: updateError } = await supabase
        .from('calls')
        .update(updates)
        .eq('id', callId);

      if (updateError) {
        console.error('Error updating call with new schedule:', updateError);
        throw updateError;
      }

      // 4. Sync with Google Calendar if event exists
      if (currentCall.google_calendar_event_id && currentCall.created_by) {
        try {
          console.log('Syncing reschedule with Google Calendar...');
          
          const startDate = new Date(newScheduledFor);
          const endDate = new Date(startDate.getTime() + 60 * 60000); // 60 minutes default

          const { error: calError } = await supabase.functions.invoke('google-calendar-update-event', {
            body: {
              host_user_id: currentCall.created_by,
              event_id: currentCall.google_calendar_event_id,
              title: currentCall.title,
              start_time: startDate.toISOString(),
              end_time: endDate.toISOString(),
              attendees: currentCall.attendees,
            },
          });

          if (calError) {
            console.error('Google Calendar sync failed:', calError);
          } else {
            console.log('Google Calendar event updated, participants notified');
          }
        } catch (calSyncError) {
          console.warn('Could not sync with Google Calendar:', calSyncError);
        }
      }

      // 5. Sync internal calendar_events table
      try {
        const originalStart = currentCall.scheduled_for;
        if (originalStart) {
          // Find matching calendar_event by contact + original start time
          let ceQuery = supabase
            .from('calendar_events')
            .select('id')
            .eq('start_at', originalStart);

          if (currentCall.contact_id) {
            ceQuery = ceQuery.eq('contact_id', currentCall.contact_id);
          }
          if (currentCall.title) {
            ceQuery = ceQuery.eq('title', currentCall.title);
          }

          const { data: ceMatch } = await ceQuery.maybeSingle();

          if (ceMatch) {
            const newStart = new Date(newScheduledFor);
            const newEnd = new Date(newStart.getTime() + 60 * 60000);

            await supabase
              .from('calendar_events')
              .update({
                start_at: newStart.toISOString(),
                end_at: newEnd.toISOString(),
                updated_at: new Date().toISOString(),
              })
              .eq('id', ceMatch.id);

            console.log('calendar_events synced for reschedule:', ceMatch.id);
          }
        }
      } catch (ceSyncError) {
        console.warn('Could not sync calendar_events on reschedule:', ceSyncError);
      }
      
      await loadCalls();
    } catch (err) {
      console.error('Error rescheduling call:', err);
      throw err;
    }
  };

  return {
    calls,
    loading,
    error,
    loadCalls,
    initiateCall,
    addCallNote,
    scheduleCall,
    updateScheduledCall,
    deleteScheduledCall,
    markCallComplete,
    cancelScheduledCall,
    rescheduleCall,
  };
}
